/* ArmorBatch Global — Feature Showcase.
   Sits between the vault door and the login screen: five large feature panels, a
   "Request a Demo" dialog that emails admin@armorbatch.com, and a "Get Started"
   button that opens the real login. Public, pre-auth, brochure only.
   Uses explicit dark industrial colours so it flows straight out of the vault
   regardless of the saved app theme. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Radar, Navigation, FileSignature, Gauge, Layers, ShieldCheck, LogIn, Mail, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { ACCESS_WORD } from "@/lib/accessKey";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ARMOR = "#0052CC";
const CONTACT = "admin@armorbatch.com";

const FEATURES = [
  {
    key: "war-room", Icon: Radar, tag: "Live operations",
    title: "War Room Dispatch",
    desc: "Command every pour from one screen — today's jobs, your whole fleet and a live traffic map side by side.",
    img: "/explore/war-room.jpg",
  },
  {
    key: "gps", Icon: Navigation, tag: "Real-time tracking",
    title: "GPS Live Tracking",
    desc: "Follow every mixer on a live map with automatic ETAs to site and back to the plant.",
    img: "/explore/gps-tracking.jpg",
  },
  {
    key: "dockets", Icon: FileSignature, tag: "Paperless delivery",
    title: "Digital Dockets",
    desc: "Each load is priced, tracked and signed on the driver's phone — no lost paper tickets.",
    img: "/explore/digital-dockets.jpg",
  },
  {
    key: "plant", Icon: Gauge, tag: "Plant cockpit",
    title: "Plant Dashboard",
    desc: "Live silo and bin levels, batch weights and daily totals straight from the batching floor.",
    img: "/explore/plant-dashboard.jpg",
  },
  {
    key: "tiers", Icon: Layers, tag: "Billing & plans",
    title: "Subscription Tiers",
    desc: "Pick the plan that fits your fleet and manage billing and seats from one place.",
    img: "https://static.prod-images.emergentagent.com/jobs/448b2756-e759-4bdc-a399-e866d3ac841b/images/6acfeb7cff97b3178a15165ef2047b186ce67d04e0db632d55cb38a5d1e13b64.jpeg",
  },
];

const inputCls = "w-full h-11 px-3 bg-black/40 border border-zinc-700 text-zinc-100 placeholder:text-zinc-600 text-sm outline-none focus:border-[#0052CC] transition-colors";
const labelCls = "mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 block";

function DemoRequestDialog({ open, onOpenChange }) {
  const [f, setF] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) return toast.error("Please add your name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return toast.error("Please add a valid email");
    setBusy(true);
    try {
      await api.post("/public/demo-request", { ...f });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send that — please email us instead");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { onOpenChange(false); setTimeout(() => { setSent(false); setF({ name: "", email: "", company: "", phone: "", message: "" }); }, 250); };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="rounded-none bg-[#11151b] border border-zinc-700 text-zinc-100 max-w-lg" data-testid="demo-request-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2" style={{ color: "#ffffff" }}>
            <Mail className="w-5 h-5" style={{ color: ARMOR }} /> Request a Demo
          </DialogTitle>
        </DialogHeader>
        {sent ? (
          <div className="py-8 text-center" data-testid="demo-request-sent">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <div className="heading text-xl font-black uppercase" style={{ color: "#ffffff" }}>Request sent</div>
            <p className="text-sm text-zinc-400 mt-2">
              Thanks {f.name.split(" ")[0] || ""} — our team will be in touch shortly. If it's urgent, email {CONTACT}.
            </p>
            <button onClick={close} data-testid="demo-request-done"
              className="mt-6 text-white uppercase tracking-widest text-xs font-bold px-6 py-3" style={{ background: ARMOR }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-zinc-400 -mt-1">Tell us a little about your operation and we'll open a private demo and quote you properly.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Your name *</label>
                <input value={f.name} onChange={set("name")} data-testid="demo-name" placeholder="Jane Smith" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input value={f.email} onChange={set("email")} data-testid="demo-email" placeholder="jane@plant.com" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Company / plant</label>
                <input value={f.company} onChange={set("company")} data-testid="demo-company" placeholder="Riverside Concrete" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input value={f.phone} onChange={set("phone")} data-testid="demo-phone" placeholder="Optional" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Anything you'd like to see?</label>
              <input value={f.message} onChange={set("message")} data-testid="demo-message" placeholder="Fleet size, plants, biggest headache…" className={inputCls} />
            </div>
            <button type="submit" disabled={busy} data-testid="demo-submit"
              className="w-full inline-flex items-center justify-center gap-2 text-white uppercase tracking-widest text-xs font-bold h-11 disabled:opacity-50" style={{ background: ARMOR }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Send request to {CONTACT}</>}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FeatureShowcase() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0d12] text-zinc-100 relative overflow-hidden" data-testid="feature-showcase">
      <style>{`
        @keyframes fsUp { from { transform: translateY(14px); } to { transform: translateY(0); } }
        .fs-up { animation: fsUp 600ms cubic-bezier(.2,.7,.3,1) both; }
        .fs-grid-bg {
          background-image:
            linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px);
          background-size: 46px 46px;
          -webkit-mask-image: radial-gradient(circle at 50% 18%, black, transparent 70%);
          mask-image: radial-gradient(circle at 50% 18%, black, transparent 70%);
        }
      `}</style>
      <div className="pointer-events-none absolute inset-0 fs-grid-bg" aria-hidden />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px]" style={{ background: ARMOR }} aria-hidden />

      {/* top bar */}
      <div className="relative flex items-center justify-between px-5 py-4 border-b border-zinc-800/80">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">ArmorBatch Global · The Global Standard</div>
        <Link to={`/login?key=${ACCESS_WORD}`} data-testid="feature-login-top"
          className="mono text-[10px] uppercase tracking-widest px-3 py-2 text-white inline-flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ background: ARMOR }}>
          <LogIn className="w-3.5 h-3.5" /> Log in
        </Link>
      </div>

      {/* hero */}
      <header className="relative text-center pt-12 pb-10 px-5">
        <div className="fs-up mx-auto w-16 h-16 flex items-center justify-center border-2" style={{ borderColor: ARMOR, background: `linear-gradient(135deg, ${ARMOR}, #003F9E)`, animationDelay: "40ms" }}>
          <ShieldCheck className="w-9 h-9 text-white" />
        </div>
        <h1 className="fs-up heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-6" style={{ animationDelay: "120ms", color: "#ffffff" }} data-testid="feature-headline">
          Armor<span style={{ color: "#4d8bff" }}>Batch</span> Global
        </h1>
        <div className="fs-up mono text-xs sm:text-sm uppercase tracking-[0.35em] text-zinc-400 mt-4" style={{ animationDelay: "200ms" }}>
          The complete concrete plant, in one console
        </div>
        <p className="fs-up text-sm sm:text-base text-zinc-400 max-w-2xl mx-auto mt-5" style={{ animationDelay: "280ms" }}>
          Dispatch, tracking, delivery, batching and billing — purpose-built for ready-mix and fleet operations.
        </p>
      </header>

      {/* feature panels */}
      <div className="relative max-w-6xl mx-auto px-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="feature-panels">
        {FEATURES.map(({ key, Icon, tag, title, desc, img }, i) => (
          <article key={key} data-testid={`feature-panel-${key}`}
            className="fs-up group relative overflow-hidden bg-[#11151b] border border-zinc-800 flex flex-col hover:border-[#0052CC] hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
            style={{ animationDelay: `${360 + i * 90}ms` }}>
            <div className="absolute top-0 left-0 h-[3px] w-0 group-hover:w-full transition-[width] duration-500 z-20" style={{ background: ARMOR }} />
            {/* real app screenshot */}
            <div className="relative h-44 overflow-hidden bg-[#0a0d12]">
              <img src={img} alt={`${title} screen`} loading="lazy"
                className="w-full h-full object-cover object-top opacity-95 group-hover:scale-[1.04] transition-transform duration-[600ms]" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,13,18,0) 0%, rgba(17,21,27,.35) 62%, #11151b 100%)" }} />
            </div>
            {/* copy */}
            <div className="relative z-10 p-7 sm:p-8 pt-5 flex flex-col flex-1">
              <div className="w-12 h-12 flex items-center justify-center border border-zinc-700 bg-black/60 group-hover:border-[#0052CC] transition-colors -mt-11 mb-4" style={{ color: "#4d8bff" }}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{tag}</div>
              <h2 className="heading text-2xl sm:text-3xl font-black uppercase leading-tight mt-1.5" style={{ color: "#ffffff" }}>{title}</h2>
              <p className="text-sm sm:text-base text-zinc-300 mt-3 leading-relaxed">{desc}</p>
            </div>
          </article>
        ))}

        {/* CTA panel fills the 6th grid cell on wide screens */}
        <article className="fs-up relative border border-dashed border-zinc-700 p-7 sm:p-8 flex flex-col justify-center gap-5"
          style={{ animationDelay: `${360 + FEATURES.length * 90}ms`, background: "linear-gradient(135deg, rgba(0,82,204,.16), rgba(0,82,204,0) 72%)" }}
          data-testid="feature-cta-panel">
          <div>
            <h2 className="heading text-2xl sm:text-3xl font-black uppercase leading-tight" style={{ color: "#ffffff" }}>See it on your plant</h2>
            <p className="text-sm text-zinc-400 mt-2">Book a walkthrough or jump straight in.</p>
          </div>
          <div className="flex flex-col gap-2.5">
            <button type="button" onClick={() => setDemoOpen(true)} data-testid="feature-request-demo"
              className="inline-flex items-center justify-center gap-2 text-white heading font-black uppercase tracking-widest text-sm px-6 py-3.5 transition-transform hover:-translate-y-0.5" style={{ background: ARMOR }}>
              <Mail className="w-4 h-4" /> Request a Demo
            </button>
            <Link to={`/login?key=${ACCESS_WORD}`} data-testid="feature-get-started"
              className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-[#0052CC] hover:text-white text-zinc-200 mono text-xs font-bold uppercase tracking-widest px-6 py-3.5 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </article>
      </div>

      {/* bottom CTA band */}
      <footer className="relative mt-14 border-t border-zinc-800/80 px-5 py-14 text-center">
        <h2 className="heading text-2xl sm:text-3xl font-black uppercase tracking-tight" style={{ color: "#ffffff" }}>Ready when you are</h2>
        <p className="text-sm text-zinc-400 mt-3 max-w-xl mx-auto">Unlimited users, unlimited dockets. We'll open a private demo and quote you properly.</p>
        <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
          <button type="button" onClick={() => setDemoOpen(true)} data-testid="feature-request-demo-footer"
            className="inline-flex items-center justify-center gap-2.5 text-white text-base font-bold px-8 py-4 transition-transform hover:-translate-y-0.5" style={{ background: ARMOR }}>
            <Mail className="w-5 h-5" /> Request a Demo
          </button>
          <Link to={`/login?key=${ACCESS_WORD}`} data-testid="feature-get-started-footer"
            className="inline-flex items-center justify-center gap-2.5 bg-white text-black hover:bg-zinc-200 text-base font-bold px-8 py-4 transition-colors">
            Get Started <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-8">{CONTACT}</div>
      </footer>

      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
